/**
 * Landeseite der Bestaetigungsmail.
 *
 * Der Client loest den Code aus der Adresse selbst ein; diese Seite sagt nur,
 * was daraus geworden ist. Drei Ausgaenge, und alle drei kommen vor:
 *
 *   bestaetigt und angemeldet  — der Normalfall
 *   bestaetigt, nicht angemeldet — Link in einem anderen Browser geoeffnet.
 *                                 PKCE bindet den Code an den Browser, der die
 *                                 Registrierung begonnen hat. Das ist kein
 *                                 Fehler, sondern der Zweck des Verfahrens.
 *   Link verbraucht oder alt   — dann hilft nur ein neuer.
 *
 * Der mittlere Fall ist der, den eine Bestaetigungsseite ueblicherweise
 * vergisst, und er trifft jeden, der Mails auf dem Telefon liest und sich am
 * Rechner registriert hat.
 *
 * SEIT 12.10.2026 (Entwurf „99takes Signup Politur"): Der Normalfall springt
 * nicht mehr nach 1,5 Sekunden weiter, sondern zeigt das gebuchte Guthaben
 * und einen Knopf. Das ist der Moment, in dem das Startguthaben ankommt, und
 * vorher sah ihn niemand. Der mittlere Fall sagt zuerst, was zu tun ist, die
 * Erklaerung steht klein darunter. Ein abgelaufener Link fuehrt zu einem
 * neuen Link statt zu „Noch einmal registrieren", das bei einer schon
 * vergebenen Adresse still nichts tut.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { ladeStatus } from '../api';
import { Feld, Hinweis, Knopf, Laedt, Rahmen, Verweis } from '../ui';
import { useSitzung } from '../session';
import { einmal } from '../messung';
import { fehlertext, supabase } from '../supabase';
import { PFAD_BESTAETIGT, redirectTo } from '../config';
import { STANDARDZIEL } from '../ziel';
import { Postfach } from './Registrieren';
import { locale, t } from '../sprache';
import { useTurnstile } from '../turnstile';

export default function Bestaetigt() {
  const { status, session } = useSitzung();
  const navigate = useNavigate();
  // Ein Fehler steht als Parameter in der Adresse, nicht im Fragment — der
  // Client raeumt das Fragment beim Einloesen weg.
  const [fehlerAusLink] = useState(() => new URLSearchParams(window.location.search).get('error_description'));
  const [guthaben, setGuthaben] = useState<number | null>(null);

  useEffect(() => {
    if (status !== 'angemeldet') return;
    // Die Registrierung ist erst hier abgeschlossen: Double-Opt-in bestaetigt.
    const id = session?.user?.id;
    if (id) einmal(`sign_up:${id}`, { event: 'sign_up', method: 'email', weg: 'bestaetigt' });
    // Das Guthaben nur aus billing/status (api.ts: nie aus der Tabelle). Ohne
    // Zahl fehlt der Kasten, die Seite bleibt sonst gleich.
    let lebt = true;
    ladeStatus()
      .then(s => { if (lebt && s.balance.metered && typeof s.balance.balance === 'number') setGuthaben(s.balance.balance); })
      .catch(() => {});
    return () => { lebt = false; };
  }, [status]);

  useEffect(() => {
    if (fehlerAusLink) console.warn('[bestaetigt] Link trägt nicht:', fehlerAusLink);
  }, [fehlerAusLink]);

  if (status === 'laedt') {
    return <Rahmen titel={t('Einen Moment', 'One moment')}><Laedt>{t('Die Bestätigung wird geprüft.', 'Checking your confirmation.')}</Laedt></Rahmen>;
  }

  if (status === 'angemeldet') {
    return (
      <Rahmen schritt={2} erledigt titel={t('Adresse bestätigt', 'Address confirmed')} unter={t('Dein Konto ist freigeschaltet, und dein Startguthaben ist gebucht.', 'Your account is active, and your starting credits have been added.')}>
        {guthaben != null && guthaben > 0 && (
          <p className="mt-0 mb-5 flex items-baseline gap-2.5 rounded-xl border border-linie bg-flaeche px-[1.125rem] py-4 lg:bg-ebene">
            <b className="font-serif text-[2.5rem] leading-none font-semibold text-marke-tief tabular-nums">{new Intl.NumberFormat(locale()).format(guthaben)}</b>
            <span className="text-[0.875rem] leading-snug text-text-leise">{t('Credits liegen auf deinem Konto.', guthaben === 1 ? 'credit is in your account.' : 'credits are in your account.')}</span>
          </p>
        )}
        <Knopf type="button" autoFocus onClick={() => navigate(STANDARDZIEL, { replace: true })}>{t('Weiter', 'Continue')}</Knopf>
        <p className="mt-2.5 mb-0 text-center text-[0.8125rem] text-text-hell">{t('Danach drei kurze Schritte: zwei Fragen, dann verbindest du 99takes mit Claude.', 'Next, three short steps: two questions, then you connect 99takes to Claude.')}</p>
      </Rahmen>
    );
  }

  if (fehlerAusLink) return <NeuerLink />;

  return (
    <Rahmen
      schritt={2}
      titel={t('Jetzt noch anmelden', 'Now sign in')}
      unter={t('Deine Adresse ist wahrscheinlich schon bestätigt. Melde dich hier einmal an, dann geht es weiter.', 'Your address is probably confirmed already. Sign in here once to continue.')}
    >
      <Knopf type="button" onClick={() => navigate('/login')}>{t('Zur Anmeldung', 'Go to sign in')}</Knopf>
      <p className="mt-5 mb-0 border-t border-linie-zart pt-4 text-[0.8125rem] leading-relaxed text-text-hell">
        {t(
          'Das passiert, wenn du den Link auf dem Telefon oder in einem anderen Browser öffnest als dem, in dem du dich registriert hast.',
          'This happens when you open the link on your phone or in a different browser from the one you signed up in.',
        )}
      </p>
    </Rahmen>
  );
}

/** Abgelaufener oder schon benutzter Link: einen neuen schicken, dann wie nach der Registrierung. */
function NeuerLink() {
  const [email, setEmail] = useState('');
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState('');
  const [verschickt, setVerschickt] = useState(false);
  const pruefung = useTurnstile('resend');

  function absenden(e: FormEvent) {
    e.preventDefault();
    setFehler('');
    pruefung.ausfuehren(schicken);
  }

  async function schicken(captchaToken: string) {
    setLaedt(true);
    // Wie bei der Registrierung antwortet Supabase auch fuer eine schon
    // bestaetigte oder unbekannte Adresse ohne Fehler. Deshalb dieselbe
    // Folgeseite, und dort der Hinweis aufs Anmelden.
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email.trim(),
      options: { emailRedirectTo: redirectTo(PFAD_BESTAETIGT), captchaToken },
    });
    setLaedt(false);
    if (error) { setFehler(fehlertext(error)); return; }
    setVerschickt(true);
  }

  if (verschickt) return <Postfach email={email.trim()} />;

  return (
    <Rahmen
      schritt={2}
      titel={t('Der Link ist abgelaufen', 'The link has expired')}
      unter={t('Bestätigungslinks gelten nur einmal und nicht lange. Wir schicken dir einen neuen.', 'Confirmation links work only once and not for long. We will send you a new one.')}
      fuss={<>{t('Schon bestätigt?', 'Already confirmed?')} <Verweis to="/login">{t('Anmelden', 'Sign in')}</Verweis></>}
    >
      <form onSubmit={absenden}>
        {fehler && <Hinweis art="schlecht">{fehler}</Hinweis>}
        <Feld
          label={t('E-Mail', 'Email')} type="email" name="email" autoComplete="email" required autoFocus
          value={email} onChange={e => setEmail(e.target.value)} disabled={laedt}
        />
        {pruefung.feld}
        <Knopf type="submit" laedt={laedt || pruefung.wartet}>{t('Neuen Link schicken', 'Send a new link')}</Knopf>
      </form>
    </Rahmen>
  );
}
